import type { Trade } from '@/models/types';
import { uniqBy } from 'lodash';
import { makeAutoObservable } from 'mobx';

class TradesStore {
  // IMPORTANT NOTE: this will be the normalized source of truth for the lenders
  // When you make changes to the lenders, you need to make sure to update this
  // If you want changes to the derived lenders data below, trigger a re-calculation
  private tradesIndexedById: Record<Trade['id'], Trade> = {};

  // IMPORTANT note: these are derived from the source of truth
  // DO NOT MAKE CHANGES TO THESE DIRECTLY
  private trades: Trade[] = [];

  constructor() {
    makeAutoObservable(this);
  }

  setTrades(trades: Trade[]) {
    this.tradesIndexedById = trades.reduce(
      (acc, tradeInAppraisal) => {
        acc[tradeInAppraisal.id] = tradeInAppraisal;
        return acc;
      },
      {} as Record<Trade['id'], Trade>,
    );

    this.trades = trades;
  }

  get getTrades() {
    return Object.values(this.tradesIndexedById);
  }

  upsert(tradeInAppraisal: Trade) {
    const existingTradeInAppraisal =
      this.tradesIndexedById[tradeInAppraisal.id];

    if (!existingTradeInAppraisal) {
      this.trades = [...this.trades, tradeInAppraisal];
    } else {
      this.tradesIndexedById[tradeInAppraisal.id] = tradeInAppraisal;
    }
  }

  remove(tradeInAppraisal: Trade) {
    this.trades = this.trades.filter((t) => t.id !== tradeInAppraisal.id);
  }

  replace(tradeInAppraisalToReplaceId: Trade['id'], tradeInAppraisal: Trade) {
    // DEV NOTE: the uniqBy is required because the BE sends a broadcast message
    // and also send the tradeInAppraisal in the payload and depending on the order of those
    // messages, we could end up with duplicate trades in the array
    // Thus, we use uniqBy to ensure that we only have one instance of the lender in the array
    this.trades = uniqBy(
      this.trades.map((t) =>
        t.id === tradeInAppraisalToReplaceId ? tradeInAppraisal : t,
      ),
      'id',
    );
  }

  updateTrade(id: Trade['id'], trade: Partial<Trade>) {
    const currentTrade = this.tradesIndexedById[id];

    this.tradesIndexedById[id] = {
      ...currentTrade,
      ...trade,
    };
  }
}

export const tradesStore = new TradesStore();
